// Close Intelligence module — self-contained API client.
// Deliberately replicates the host base-URL resolution (rather than importing
// apiService) so the module can be lifted out with zero host imports.
import axios from 'axios';

const resolveApiBaseUrl = () => {
  const envBaseUrl = (process.env.REACT_APP_API_BASE_URL || '').trim();
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const isHosted =
      host === 'bankanalysis.aceanalytics.dev' ||
      host.endsWith('.aceanalytics.dev') ||
      host.endsWith('.vercel.app');
    if (isHosted) return '/api';
  }
  if (envBaseUrl) return envBaseUrl.replace(/\/$/, '');
  return 'http://localhost:5001/api';
};

const client = axios.create({ baseURL: `${resolveApiBaseUrl()}/close-intel` });

const ACTOR_STORAGE_KEY = 'rfm-active-user';
export const activeActor = () => {
  if (typeof window === 'undefined') return 'FP&A Analyst';
  try {
    const raw = window.localStorage.getItem(ACTOR_STORAGE_KEY) || 'FP&A Analyst';
    return raw.split(' — ')[0].trim();
  } catch (e) {
    return 'FP&A Analyst';
  }
};

const errText = (error) => {
  const detail = error?.response?.data?.error ?? error?.message ?? error;
  return typeof detail === 'string' ? detail : JSON.stringify(detail);
};

const call = async (fn) => {
  try {
    const res = await fn();
    return res.data;
  } catch (error) {
    const wrapped = new Error(errText(error));
    wrapped.status = error?.response?.status;
    throw wrapped;
  }
};

const closeIntelApi = {
  health: () => call(() => client.get('/health')),
  runMonitor: (payload = {}) =>
    call(() => client.post('/monitor/run', { createdBy: activeActor(), ...payload })),
  listFindings: (params = {}) => call(() => client.get('/findings', { params })),
  findingContext: (findingId) => call(() => client.get(`/findings/${findingId}/context`)),
  judgeFinding: (findingId) => call(() => client.post(`/findings/${findingId}/judge`)),
  judgeBatch: (payload = {}) => call(() => client.post('/findings/judge-batch', payload)),
  dispositionFinding: (findingId, payload) =>
    call(() => client.post(`/findings/${findingId}/disposition`, { createdBy: activeActor(), ...payload })),
  agentMemory: (params = {}) => call(() => client.get('/memory', { params })),
  listRules: (params = {}) => call(() => client.get('/suppression-rules', { params })),
  saveRule: (payload) =>
    payload.id
      ? call(() => client.put(`/suppression-rules/${payload.id}`, payload))
      : call(() => client.post('/suppression-rules', payload)),
  listRuns: (params = {}) => call(() => client.get('/runs', { params })),
  listExpectations: (kind, params = {}) => call(() => client.get(`/expectations/${kind}`, { params })),
  saveExpectation: (kind, payload) =>
    call(() => client.post(`/expectations/${kind}`, { createdBy: activeActor(), ...payload })),
  // CI_03 read-only unit forecast baselines. `params` is the object produced by
  // forecastContextHelpers.buildForecastContextParams (period, basis, market,
  // lob, entity, metricKey, limit, offset).
  listForecastContextUnits: (params = {}) =>
    call(() => client.get('/forecast-context/units', { params })),
  // CI_03 audited overrides. List honors period/sourceVersion/entity/metricKey/basis
  // filters; save attaches the active actor exactly like saveExpectation.
  listForecastOverrides: (params = {}) =>
    call(() => client.get('/forecast-context/overrides', { params })),
  saveForecastOverride: (payload) =>
    call(() => client.post('/forecast-context/overrides', { createdBy: activeActor(), ...payload })),
  runClose: (payload = {}) =>
    call(() => client.post('/close/run', { createdBy: activeActor(), ...payload })),
  continueClose: (runId, payload = {}) =>
    call(() => client.post(`/close/${runId}/narrate`, payload)),
  board: (runId, params = {}) => call(() => client.get(`/close/${runId}/board`, { params })),
  bridge: (runId, params = {}) => call(() => client.get(`/close/${runId}/bridge`, { params })),
  evidence: (runId) => call(() => client.get(`/close/${runId}/evidence`)),
};

export default closeIntelApi;
